import {yupResolver} from '@hookform/resolvers/yup'
import {
    Box,
    Dialog, DialogActions, DialogContent,
    Typography
} from '@mui/material'
import {useEffect, useState} from 'react'
import {FieldValues, useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import {useRecoilValue, useSetRecoilState} from 'recoil'
import {
    CoreImportService,
    PayrollImportData
} from '../../../../Component/Api/Import/CoreImportService'
import {IDropDownOptions} from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList'
import {GetAllScorecardsResponse} from '../../../../Component/Model/Dto/ScorecardLicense'
import {ExportKeyValue} from '../../../../Component/Model/Dto/Upload'
import {ImportSource, RequestStatus, StagingEntityName} from '../../../../Component/Model/Enum/ImportEnum'
import {defaultObject} from '../../../../Helpers/typescriptHelper'
import {
    VerificationWorkflowService
} from '../../../../Component/Api/VerificationAgency/VerificationWorkflow/VerificationWorkflowService'
import {
    BEE123SalariesEditRole,
    EE123PeopleRole_Edit,
    EE123SalariesRole_Edit,
    PeopleEditRole
} from '../../../../Component/Common/Constants/UserRoles'
import {userRolesState} from '../../../../State/user.Atom'
import {OrganisationAtom} from "../../../../State/Organisation.Atom";
import {OrganisationService} from "../../../../Component/Api/Organisation/OrganisationService";
import {RequestTrackerService} from "../../../../Component/Api/Staging/RequestTrackerService";
import {RequestTracker} from "../../../../Component/Model/Dto/Staging/RequestTracker";
import {LockOutlined} from "@mui/icons-material";
import {useHistory} from "react-router-dom";
import {
    ImportManagement
} from "../../../../Component/Model/Dto/VerificationAgency/Client/ImportManagement/ImportManagement";
import {PayrollImportValidationSchema} from "./PayrollImportValidationSchema";
import {CustomDialogTitle} from "../../../Common/Custom/CustomDialogTitle";
import {MDBox} from "../../../../Component/Common/Components/MDBox";
import {MDTypography} from "../../../../Component/Common/Components/MDTypography";
import {MDButton} from "../../../../Component/Common/Components/MDButton";
import {SkeletonLoading} from "../../../../Component/Common/SkeletonLoading/SkeletonLoading";
import {useSignalRConnection} from "../../../../Hooks/useSignalRConnection";
import {analytics} from '../../../../Services/analytics';
import {SourceStep} from "./SourceStep";
import {FetchedDataStep} from "./FetchedDataStep";
import {DestinationStep} from "./DestinationStep";
import {ConfirmStep} from "./ConfirmStep";
import {ResultsStep} from "./ResultsStep";

const stepsMaxWidth = 900;

export const PayrollImport = () => {
    const setOrganisationAtom = useSetRecoilState(OrganisationAtom);
    const organisation = useRecoilValue(OrganisationAtom);
    const [scorecardData, setScorecardData] = useState<GetAllScorecardsResponse>(defaultObject);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [progressData, setProgressData] = useState<{
        [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto }
    }>({});
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [requestTracker, setRequestTracker] = useState<RequestTracker.RequestTrackerDetail>();
    const [organisations, setOrganisations] = useState<IDropDownOptions[]>([]);
    const [importDetails, setImportDetails] = useState<ImportManagement.ImportDetailDto[]>([]);
    const history = useHistory();
    const [useSignalR, setUseSignalR] = useState<boolean>(false);
    const [isImportInProgressForOrganisation, setIsImportInProgressForOrganisation] = useState<boolean>(false);
    const salaryFetchAvailable = requestTracker?.hasStagedSalaries === true;
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const hub = useSignalRConnection("ImportHub");
    const stagingHub = useSignalRConnection("stagingHub");

    const {control, handleSubmit, watch, reset, setValue, trigger} = useForm({
        mode: 'all',
        resolver: yupResolver(PayrollImportValidationSchema)
    });

    const selectedOrganisation = watch('organisationId');
    const selectedBEEScorecardIds: string[] = watch('beeScorecards') ?? [];
    const selectedEEScorecardIds: string[] = watch('eeScorecards') ?? [];
    const selectedScorecardIds: string[] = [
        ...selectedBEEScorecardIds,
        ...selectedEEScorecardIds
    ];
    const includeSalaries = watch('includeSalaries') === 1;
    const userRoles = useRecoilValue(userRolesState);
    const hasBEEImportRole = userRoles.includes(PeopleEditRole);
    const hasEEImportRole = userRoles.includes(EE123PeopleRole_Edit);
    const hasBEESalaryEditRole = userRoles.includes(BEE123SalariesEditRole);
    const hasEESalaryEditRole = userRoles.includes(EE123SalariesRole_Edit);
    const canImportSalaries =
        (selectedBEEScorecardIds.length === 0 || hasBEESalaryEditRole) &&
        (selectedEEScorecardIds.length === 0 || hasEESalaryEditRole);
    const employeeRecordCount = requestTracker?.stagingDetails
        ?.find(detail => detail.entityName === StagingEntityName.EmployeeStructure)?.totalRecords;

    const findScorecardName = (scorecardId: string): string => {
        const allScorecards = [
            ...(scorecardData.beeScorecards ?? []),
            ...(scorecardData.eeScorecards ?? [])
        ];
        return allScorecards.find(x => x.scorecardId === scorecardId)?.scorecardName ?? "";
    };

    useEffect(() => {
        getOrganisations();
        if (organisation.OrganisationId > 0) {
            const updatedOrganisationData = {
                ...defaultObject,
                organisationId: organisation.OrganisationId
            };
            reset(updatedOrganisationData);
        }
    }, []);

    const loadOrganisationData = async (organisationId: number) => {
        setIsLoading(true);
        try {
            await getOrganisationRelatedData(organisationId);
            await getOrganisationImportsInProgress(organisationId);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOrganisation !== undefined && selectedOrganisation > 0) {
            loadOrganisationData(selectedOrganisation);
            setValue("beeScorecards", []);
            setValue("eeScorecards", []);
            setValue("includeSalaries", 0);
        } else {
            setRequestTracker(undefined);
        }
    }, [selectedOrganisation]);

    useEffect(() => {
        if (!canImportSalaries) {
            setValue("includeSalaries", 0);
        }
    }, [canImportSalaries]);

    useEffect(() => {
        if (selectedOrganisation && organisations.length > 0) {
            const OrganisationName = organisations.find(f => f.id == selectedOrganisation)?.value ?? "";
            setOrganisationAtom({
                OrganisationId: selectedOrganisation,
                OrganisationName,
                ShowPayrollIntegrationMenuItems: true
            });
        }
    }, [selectedOrganisation, organisations]);

    const getOrganisations = async () => {
        try {
            setIsLoading(true);
            const organisationResponse = await OrganisationService.getOrganisationsWithPayroll();

            const organisationData: IDropDownOptions[] = organisationResponse.data
                .filter(item => item.organisationId !== undefined)
                .map(item => ({
                    id: item.organisationId!,
                    value: item.registrationName,
                }));

            setOrganisations(organisationData);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const getOrganisationRelatedData = async (organisationId: number) => {
        try {
            const scorecardResponse = await OrganisationService.getOrganisationScorecards(organisationId);
            const requestTrackerResponse = await RequestTrackerService.getLatestRequestTracker(organisationId)

            setScorecardData(scorecardResponse.data);
            if (requestTrackerResponse.data) {
                setRequestTracker(requestTrackerResponse.data);
            } else {
                setRequestTracker(undefined);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const getOrganisationImportsInProgress = async (organisationId: number) => {
        try {
            const organisationImportDetails = await VerificationWorkflowService.GetOrganisationImportDetails(organisationId, ImportSource.Payroll);

            if (organisationImportDetails.data?.length > 0) {
                const initialProgressData: {
                    [scorecardId: string]: { [importTypeId: number]: ImportManagement.ImportDetailSheetDto }
                } = {};

                organisationImportDetails.data.forEach(importDetail => {
                    const scorecardId = importDetail.scorecardId;
                    initialProgressData[scorecardId] = {};

                    importDetail.progressSheets?.forEach(sheet => {
                        initialProgressData[scorecardId][sheet.importTypeId] = {...sheet};
                    });
                });

                setProgressData(initialProgressData);
                setImportDetails(organisationImportDetails.data);
                setUseSignalR(true);
                setIsImportInProgressForOrganisation(true);
            } else {
                setIsImportInProgressForOrganisation(false);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleDataSubmit = async (data: FieldValues) => {
        setProgressData(defaultObject);
        setIsImporting(true);
        try {
            const parameters: ExportKeyValue[] = [];
            data.eeScorecards = data.eeScorecards || [];
            data.beeScorecards = data.beeScorecards || [];
            const scorecardIds = [...data.beeScorecards, ...data.eeScorecards];

            const inProgressScorecards: string[] = [];
            for (const scorecardId of scorecardIds) {

                const isInProgress = await checkForInProgressImport(scorecardId);
                if (isInProgress) {
                    inProgressScorecards.push(scorecardId);

                }
            }
            if (inProgressScorecards.length > 0) {
                for (const id of inProgressScorecards) {
                    toast.error(`An import is already in progress for scorecard: ${findScorecardName(id)}`);
                }
                setIsImporting(false);
                return;
            }

            const request: PayrollImportData = {
                beeScorecardIds: data.beeScorecards,
                eeScorecardIds: data.eeScorecards,
                parameters: parameters,
                organisationId: selectedOrganisation,
                requestId: requestTracker?.requestId ?? 0,
                includeSalaries: data.includeSalaries === 1
            };
            const result = await CoreImportService.ImportPayrollData(request);
            if (result.data) {
                analytics.cdmPayrollDataImported();
                getOrganisationImportsInProgress(selectedOrganisation);
            }

        } catch (error) {
            console.log(error);
            toast.error('Import Failed');
        } finally {
            setIsImporting(false);
        }
    }

    const updateProgress = (
        scorecardId: string,
        importTypeId: number,
        updates: Partial<ImportManagement.ImportDetailSheetDto>
    ) => {
        setProgressData(prev => ({
            ...prev,
            [scorecardId]: {
                ...(prev[scorecardId] ?? {}),
                [importTypeId]: {
                    ...(prev[scorecardId]?.[importTypeId] ?? {}),
                    ...updates,
                },
            },
        }));
    };

    useEffect(() => {
        if (!useSignalR) return;

        const handlers: { eventName: string; callback: (...args: any[]) => void }[] = [];

        const registerHandler = (eventName: string, callback: (...args: any[]) => void) => {
            hub.on(eventName, callback);
            handlers.push({ eventName, callback });
        };

        (async () => {
            try {
                if (!hub.isConnected) {
                    await hub.start();
                }

                importDetails.forEach(importDetail => {
                    const scorecardId = importDetail.scorecardId;
                    const importManagementId = importDetail.id;
                    const identifier = `payroll-${scorecardId}-${selectedOrganisation}-${importManagementId}`;

                    registerHandler(
                        `ImportInProgress-${identifier}`,
                        data => {
                            const { CurrentScorecard, ImportTypeId, progress, LastSuccessfulStep } = data;
                            if (!CurrentScorecard || ImportTypeId == null) return;
                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: progress,
                                lastSuccessfulStep: LastSuccessfulStep,
                            });
                        }
                    );

                    registerHandler(
                        `ImportSuccess-${identifier}`,
                        data => {
                            const { CurrentScorecard, ImportTypeId, NumberOfRowsCorrect, NumberOfRowsFailed, Log } = data;
                            if (!CurrentScorecard || ImportTypeId == null) return;
                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: 100,
                                importSuccessful: true,
                                numberOfRowsCorrect: NumberOfRowsCorrect,
                                numberOfRowsFailed: NumberOfRowsFailed,
                                log: Log,
                            });
                        }
                    );

                    registerHandler(
                        `ImportFailed-${identifier}`,
                        data => {
                            const {
                                CurrentScorecard,
                                ImportTypeId,
                                NumberOfRowsCorrect,
                                NumberOfRowsFailed,
                                Log,
                                ErrorBlobUrl,
                                LastSuccessfulStep,
                                isCompleted
                            } = data;

                            if (!CurrentScorecard || ImportTypeId == null) return;
                            updateProgress(CurrentScorecard, ImportTypeId, {
                                progressPercentage: 0,
                                importSuccessful: false,
                                numberOfRowsCorrect: NumberOfRowsCorrect,
                                numberOfRowsFailed: NumberOfRowsFailed,
                                log: Log,
                                errorBlobUrl: ErrorBlobUrl,
                                lastSuccessfulStep: LastSuccessfulStep,
                            });

                            if (isCompleted !== undefined && !isCompleted) {
                                toast.error(`Import Failed for ${importDetail.scorecardName}. Please try again`);
                            }
                        }
                    );
                });

                registerHandler(
                    `ImportFailed-payroll-${selectedOrganisation}`,
                    () => {
                        toast.error(`Import Failed for selected scorecards. Please try again`);
                        setIsImportInProgressForOrganisation(false);
                    }
                );

                registerHandler(
                    `ImportComplete-payroll-${selectedOrganisation}`,
                    () => {
                        setIsImportInProgressForOrganisation(false);
                    }
                );

            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();

        return () => {
            handlers.forEach(h => hub.off(h.eventName, h.callback));
        };
    }, [useSignalR, importDetails]);

    useEffect(() => {
        if (!selectedOrganisation || selectedOrganisation <= 0) return;

        const eventName = `payrollRequest-${selectedOrganisation}`;
        const handler = () => {
            getOrganisationRelatedData(selectedOrganisation);
        };

        (async () => {
            try {
                if (!stagingHub.isConnected) {
                    await stagingHub.start();
                }
                stagingHub.on(eventName, handler);
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();

        return () => {
            stagingHub.off(eventName, handler);
        };
    }, [selectedOrganisation]); // eslint-disable-line react-hooks/exhaustive-deps

    const requestEmployees = async () => {
        setIsLoading(true);
        try {
            const response = await RequestTrackerService.requestEmployees(selectedOrganisation);
            if (response.data) {
                toast.success("Successfully requested employees.");
                getOrganisationRelatedData(selectedOrganisation);
            } else {
                toast.error(`A request to fetch employees is already in progress.`);
            }

        } catch (error) {
            console.log(error)
            toast.error("An error has occurred");
        } finally {
            setIsLoading(false);
        }
    }

    const checkForInProgressImport = async (scorecardId: string): Promise<boolean> => {
        try {
            const result = await VerificationWorkflowService.IsImportInProgressForScorecard(scorecardId);
            return !!result.data;
        } catch (error) {
            console.log(error);
            toast.error("An error has occurred");
            return false;
        }
    };

    const openFetchDataScreen = () => {
        const OrganisationName = organisations.find(f => f.id == selectedOrganisation)?.value ?? "";
        setOrganisationAtom({
            OrganisationId: selectedOrganisation ?? -1,
            OrganisationName,
            ShowPayrollIntegrationMenuItems: true
        });
        history.push(`/organisation/payrollData/fetchData`);
    }

    const hasScorecards = (): boolean => {
        return scorecardData.eeScorecards?.length > 0 || scorecardData.beeScorecards?.length > 0;
    }

    const disableImportButton = (): boolean => {
        // Import needs a completed fetch (ImportReady) and scorecards. Validation is advisory only (the badge + the
        // review screen) and never gates the import - the real import validates every row itself.
        return !(hasScorecards() && requestTracker?.status === RequestStatus.ImportReady);
    }

    const handleOpenConfirmation = async () => {
        // validates the form
        const isValid = await trigger();
        if (isValid) {
            setShowConfirmationDialog(true); //
        }
    };

    if (isLoading) {
        return <SkeletonLoading formLoading FieldAmount={6} width={"100%"}/>
    }
        return (
            <>
                {organisations.length == 0 &&
                    <Box style={{
                        position: 'absolute',
                        left: 0,
                        width: '100%',
                        height: '80%',
                        backgroundColor: '#05050585',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column'
                    }}>
                        <Box mb={2}>
                            <LockOutlined style={{color: 'white', width: 150, height: 150}}/>
                        </Box>
                        <Typography variant='h3' fontWeight='bold' style={{color: 'white'}}>
                            There are no payrolls connected to your organisation(s)
                        </Typography>
                        <Typography variant='h3' fontWeight='bold' style={{color: 'white'}}>
                            Please connect one or contact your administrator.
                        </Typography>
                    </Box>
                }

                <Typography variant='h4' fontWeight='bold'>
                    Central Data Management - Payroll Imports
                </Typography>
                <Typography variant='body2' color="secondary" sx={{maxWidth: stepsMaxWidth, pb: 3}}>
                    Here you can import data fetched from your payroll for an organisation into the scorecard(s) you
                    select.
                </Typography>

                <Box component="form" id='PayrollImportForm' sx={{maxWidth: stepsMaxWidth}}
                     onSubmit={handleSubmit(handleDataSubmit)}>

                    <SourceStep
                        control={control}
                        organisations={organisations}
                        selectedOrganisation={selectedOrganisation}
                        hasFetchedData={requestTracker !== undefined}
                        salaryFetchAvailable={salaryFetchAvailable}
                        canImportSalaries={canImportSalaries}
                        disabled={isImporting || isImportInProgressForOrganisation}/>

                    <FetchedDataStep
                        selectedOrganisation={selectedOrganisation}
                        requestTracker={requestTracker}
                        requestEmployees={requestEmployees}
                        openFetchDataScreen={openFetchDataScreen}/>

                    <DestinationStep
                        control={control}
                        selectedOrganisation={selectedOrganisation}
                        scorecardData={scorecardData}
                        selectedScorecardIds={selectedScorecardIds}
                        findScorecardName={findScorecardName}
                        requestTracker={requestTracker}
                        employeeRecordCount={employeeRecordCount}
                        includeSalaries={includeSalaries}
                        hasBEEImportRole={hasBEEImportRole}
                        hasEEImportRole={hasEEImportRole}
                        disabled={isImporting || isImportInProgressForOrganisation}/>

                    <ConfirmStep
                        selectedOrganisation={selectedOrganisation}
                        selectedScorecardIds={selectedScorecardIds}
                        findScorecardName={findScorecardName}
                        requestTracker={requestTracker}
                        employeeRecordCount={employeeRecordCount}
                        includeSalaries={includeSalaries}
                        isImportReady={requestTracker?.status === RequestStatus.ImportReady}
                        isImporting={isImporting}
                        isImportInProgressForOrganisation={isImportInProgressForOrganisation}
                        disableImport={disableImportButton() || isImportInProgressForOrganisation}
                        onBeginImport={handleOpenConfirmation}/>

                    <ResultsStep
                        importDetails={importDetails}
                        progressData={progressData}
                        useSignalR={useSignalR}
                        isImportInProgressForOrganisation={isImportInProgressForOrganisation}/>
                </Box>

                <Dialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)}
                        aria-labelledby="form-dialog-title" maxWidth='sm'>
                    <CustomDialogTitle id="customized-dialog-title"
                                       onClose={() => setShowConfirmationDialog(false)}>
                        {includeSalaries ? 'Import Employees and Salaries' : 'Import Employees'}
                    </CustomDialogTitle>
                    <DialogContent>
                        <MDBox>
                            <MDTypography variant="button" fontWeight='regular' fontSize="0.9em"
                                          sx={{whiteSpace: 'pre-line'}}>
                                {includeSalaries
                                    ? 'Note that importing will update both the Employee Structure and Employee Salaries with the latest information fetched from your payroll.'
                                    : 'Note that importing will update the Employee Structure only. Existing salary data on your scorecards will not be changed.'}
                            </MDTypography>
                        </MDBox>
                    </DialogContent>
                    <DialogActions>
                        <MDButton
                            color="primary"
                            variant="contained"
                            onClick={() => {
                                handleSubmit(handleDataSubmit)();
                                setShowConfirmationDialog(false);
                            }}>
                            Import
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </>
        )
}
